/**
 * sl-yphr.14: FullscreenRegistry
 *
 * Generic registry for fullscreen view types (Database, ELSTER, Workflow, etc.).
 * Each view type registers once; the registry manages which view is active.
 *
 * Pattern: Open/Closed Principle — new fullscreen views register() without
 * modifying existing code. Single Source of Truth for active fullscreen state.
 */

import type { Store } from '@blocksuite/affine/store';
import { LiveData, Service } from '@toeverything/infra';
import type { FC, ReactElement } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface FullscreenViewConfig {
  /** Unique identifier, e.g. 'database', 'elster', 'workflow' */
  id: string;
  /** Icon for header button */
  icon: ReactElement;
  /** Tooltip / label for header button */
  label: string;
  /** Keyboard shortcut, e.g. 'Alt+T' */
  shortcut?: string;
  /** React component rendered inside the fullscreen modal */
  component: FC<FullscreenViewProps>;
  /** Hide the generic modal close button when the view provides its own close affordance. */
  hideModalClose?: boolean;
  /** Whether this view should show its header button (reactive gate) */
  isAvailable: () => boolean;
  /**
   * Find the default block ID to open when the header button is clicked.
   * Returns null if no suitable block exists in the current doc; a finder
   * that must load page parts first resolves later.
   */
  findDefaultBlockId: (doc: Store) => string | null | Promise<string | null>;
  /** Order in header button list (lower = left) */
  order?: number;
}

export interface FullscreenViewProps {
  blockId: string;
  payload?: unknown;
  onClose: () => void;
}

export interface ActiveFullscreenView {
  viewId: string;
  blockId: string;
  payload?: unknown;
}

export type FullscreenCloseGuard = () => boolean | Promise<boolean>;

// =============================================================================
// Service
// =============================================================================

export class FullscreenRegistry extends Service {
  private readonly configs = new Map<string, FullscreenViewConfig>();

  private pendingOpen: ActiveFullscreenView | null = null;

  /** All registered view configs */
  readonly registeredViews$ = new LiveData<FullscreenViewConfig[]>([]);

  /** Currently active fullscreen view (null = closed) */
  readonly activeView$ = new LiveData<ActiveFullscreenView | null>(null);

  /** Flag: last close was user-initiated (ESC, X, backdrop) vs programmatic (navigation guard) */
  private _userInitiatedClose = false;

  private closeGuard: FullscreenCloseGuard | null = null;

  private closeGuardPending = false;

  /** Self-contained event handler for database fullscreen events from BlockSuite layer */
  private readonly _handleDatabaseFullscreen = (e: Event) => {
    const { databaseId, viewId } = (
      e as CustomEvent<{ databaseId: string; viewId?: string }>
    ).detail;
    if (databaseId) {
      this.open(
        'database',
        databaseId,
        viewId ? { databaseViewId: viewId } : undefined
      );
    }
  };

  private readonly _handleAttachmentFullscreen = (e: Event) => {
    const { attachmentId, source } = (
      e as CustomEvent<{ attachmentId?: string; source?: { id?: string } }>
    ).detail;
    if (source?.id) {
      this.open('attachment', source.id, source);
      return;
    }
    if (attachmentId) {
      this.open('attachment', attachmentId);
    }
  };

  constructor() {
    super();
    // Listen for database fullscreen events directly — bypasses EventRouter
    // which may not be instantiated due to DI scope/timing issues.
    window.addEventListener(
      'streamlain:database-fullscreen',
      this._handleDatabaseFullscreen
    );
    window.addEventListener(
      'streamlain:attachment-fullscreen',
      this._handleAttachmentFullscreen
    );
  }

  override dispose(): void {
    window.removeEventListener(
      'streamlain:database-fullscreen',
      this._handleDatabaseFullscreen
    );
    window.removeEventListener(
      'streamlain:attachment-fullscreen',
      this._handleAttachmentFullscreen
    );
  }

  /**
   * Register a new fullscreen view type.
   * Idempotent — re-registering the same ID replaces the config.
   */
  register(config: FullscreenViewConfig): void {
    this.configs.set(config.id, config);
    this.registeredViews$.next(Array.from(this.configs.values()));
    if (this.pendingOpen?.viewId === config.id) {
      const pendingOpen = this.pendingOpen;
      this.pendingOpen = null;
      this.open(pendingOpen.viewId, pendingOpen.blockId, pendingOpen.payload);
    }
  }

  /** Open a fullscreen view by ID and block ID (idempotent) */
  open(viewId: string, blockId: string, payload?: unknown): void {
    if (!this.configs.has(viewId)) {
      this.pendingOpen = payload
        ? { viewId, blockId, payload }
        : { viewId, blockId };
      return;
    }
    const current = this.activeView$.value;
    if (
      current?.viewId === viewId &&
      current?.blockId === blockId &&
      current?.payload === payload
    ) {
      return;
    }
    const next = payload ? { viewId, blockId, payload } : { viewId, blockId };
    if (!current) {
      this.activeView$.next(next);
      return;
    }
    this._runCloseGuard(() => {
      this.activeView$.next(next);
    });
  }

  /** Return a deferred open that is waiting for its view registration. */
  getPendingOpen(): ActiveFullscreenView | null {
    return this.pendingOpen;
  }

  /** Close the active fullscreen view (programmatic — e.g. navigation guard) */
  close(): void {
    this._runCloseGuard(() => {
      this.pendingOpen = null;
      this.activeView$.next(null);
    });
  }

  /** Close triggered by explicit user action (ESC, X button, backdrop click) */
  closeByUser(): void {
    this._runCloseGuard(() => {
      this._userInitiatedClose = true;
      this.pendingOpen = null;
      this.activeView$.next(null);
    });
  }

  setCloseGuard(guard: FullscreenCloseGuard): () => void {
    this.closeGuard = guard;
    return () => {
      if (this.closeGuard === guard) {
        this.closeGuard = null;
      }
    };
  }

  /** Consume and reset the user-initiated close flag. Returns true once after closeByUser(). */
  consumeUserCloseFlag(): boolean {
    const val = this._userInitiatedClose;
    this._userInitiatedClose = false;
    return val;
  }

  /** Get config for a specific view */
  getConfig(viewId: string): FullscreenViewConfig | undefined {
    return this.configs.get(viewId);
  }

  private _runCloseGuard(commit: () => void): void {
    if (this.closeGuardPending) return;
    const guard = this.closeGuard;
    if (!guard) {
      commit();
      return;
    }

    this.closeGuardPending = true;
    void Promise.resolve()
      .then(() => guard())
      .then(allowed => {
        if (allowed) commit();
      })
      .catch(error => {
        console.error('[fullscreen-registry] Close guard failed', error);
      })
      .finally(() => {
        this.closeGuardPending = false;
      });
  }
}
